import { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    fetchStoreCreditHistory,
    grantStoreCredit,
    adjustStoreCredit,
    type StoreCreditEntry,
} from "../api/storeCreditApi";

type Props = {
    customerId: number | null;
    customerName?: string;
};

const TYPE_LABEL: Record<string, string> = {
    grant: "Grant",
    refund: "Refund",
    redemption: "Redemption",
    reversal: "Reversal",
    adjustment: "Adjustment",
};

// Balance + grant/adjust controls + ledger history for a single customer.
// Shared by StoreCreditDialog (standalone) and the User Details "Store Credit" tab.
export default function StoreCreditPanel({ customerId, customerName }: Props) {
    const [balance, setBalance] = useState(0);
    const [entries, setEntries] = useState<StoreCreditEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [mode, setMode] = useState<"grant" | "adjust">("grant");
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");

    const load = useCallback(async () => {
        if (!customerId) return;
        setLoading(true);
        setError("");
        try {
            const data = await fetchStoreCreditHistory(customerId);
            setBalance(data.balance);
            setEntries(data.entries);
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to load store credit.");
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        setAmount("");
        setReason("");
        setMode("grant");
        load();
    }, [load]);

    const handleSubmit = async () => {
        if (!customerId) return;
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt === 0) {
            setError("Enter a non-zero amount.");
            return;
        }
        if (mode === "grant" && amt < 0) {
            setError("Grant amount must be positive. Use Adjust for deductions.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            if (mode === "grant") {
                await grantStoreCredit(customerId, amt, reason.trim() || undefined);
            } else {
                await adjustStoreCredit(customerId, amt, reason.trim() || undefined);
            }
            setAmount("");
            setReason("");
            await load();
        } catch (err: any) {
            setError(err?.response?.data?.error || "Failed to update store credit.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Current balance{customerName ? ` — ${customerName}` : ""}
                </Typography>
                <Typography variant="h5" fontWeight={800}>${balance.toFixed(2)}</Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1 }}>
                <TextField
                    select
                    size="small"
                    label="Action"
                    value={mode}
                    onChange={(e) => setMode(e.target.value as "grant" | "adjust")}
                    sx={{ width: { xs: "100%", sm: 150 } }}
                    disabled={submitting}
                >
                    <MenuItem value="grant">Grant (+)</MenuItem>
                    <MenuItem value="adjust">Adjust (+/-)</MenuItem>
                </TextField>
                <TextField
                    size="small"
                    label="Amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={submitting}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    inputProps={{ step: "0.01" }}
                    sx={{ width: { xs: "100%", sm: 160 } }}
                />
                <TextField
                    size="small"
                    label="Reason (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={submitting}
                    fullWidth
                />
            </Stack>
            <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !amount}
                sx={{ mb: 2 }}
            >
                {submitting ? "Saving..." : mode === "grant" ? "Add credit" : "Apply adjustment"}
            </Button>

            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>History</Typography>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : entries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No store-credit activity yet.</Typography>
            ) : (
                <Stack spacing={1}>
                    {entries.map((e) => {
                        const positive = Number(e.amount) >= 0;
                        return (
                            <Box
                                key={e.id}
                                sx={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    gap: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip size="small" label={TYPE_LABEL[e.type] || e.type} />
                                        {e.order_id ? (
                                            <Typography variant="caption" color="text.secondary">order #{e.order_id}</Typography>
                                        ) : null}
                                    </Stack>
                                    {e.reason ? (
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {e.reason}
                                        </Typography>
                                    ) : null}
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(e.created_at).toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                    <Typography fontWeight={700} color={positive ? "success.main" : "error.main"}>
                                        {positive ? "+" : "-"}${Math.abs(Number(e.amount)).toFixed(2)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        bal ${Number(e.balance_after).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}
